import api from "../api/axios";
import { companyID } from "../auth";

const BASE_PATH = "/recruitment/candidates";
const BASE_PATH_CANDIDATES = "/api/candidates";

const ensureCompanyId = () => {
  const id = companyID();
  if (!id) {
    throw new Error("Company ID not found");
  }
  return id;
};

const sanitizeParams = (params = {}) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === "") {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
};

export const fetchCandidates = async (params = {}) => {
  const company_id = ensureCompanyId();
  
  // Transform page/pageSize to limit/offset
  const transformedParams = { ...params };
  if (transformedParams.page && transformedParams.per_page) {
    const page = parseInt(transformedParams.page) || 1;
    const per_page = parseInt(transformedParams.per_page) || 10;
    transformedParams.limit = per_page;
    transformedParams.offset = (page - 1) * per_page;
    delete transformedParams.page;
    delete transformedParams.per_page;
  }
  
  const { data } = await api.get(`${BASE_PATH_CANDIDATES}/company/${company_id}`, {
    params: sanitizeParams(transformedParams),
  });
  return data;
};

export const registerCandidate = async (payload) => {
  const company_id = ensureCompanyId();
  const formData = new FormData();
  formData.append("company_id", company_id);
  Object.entries({ ...payload }).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  const { data } = await api.post(`${BASE_PATH_CANDIDATES}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const fetchCandidateDetail = async (candidateId) => {
  const { data } = await api.get(`${BASE_PATH_CANDIDATES}/${candidateId}?company_id=${ensureCompanyId()}`);
  return data;
};

export const updateCandidate = async (candidateId, payload) => {
  const formData = new FormData();
  Object.entries({ ...payload }).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  const { data } = await api.put(`${BASE_PATH_CANDIDATES}/${candidateId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const updateCandidateStatus = async (candidateId, status, note) => {
  const { data } = await api.patch(
    `${BASE_PATH_CANDIDATES}/${candidateId}/status`,
    { status, note }
  );
  return data;
};

export const sendCandidateDocument = async (candidateId, payload) => {
  const company_id = ensureCompanyId();
  const formData = new FormData();
  formData.append("company_id", company_id);
  formData.append("candidate_id", candidateId);
  formData.append("source", "uploaded");
  
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  
  const { data } = await api.post(
    "/api/v2/documents/create",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
};

export const fetchCandidateTimeline = async (candidateId) => {
  const company_id = ensureCompanyId();
  const { data } = await api.get(
    `${BASE_PATH}/${company_id}/${candidateId}/timeline`
  );
  return data;
};

export const fetchDepartments = async () => {
  const company_id = ensureCompanyId();
  const { data } = await api.get(`department/${company_id}`);
  return data;
};

export const fetchHRList = async () => {
  const company_id = ensureCompanyId();
  const { data } = await api.get(`employees/employeeDetails/${company_id}`, {
    params: { role: 'HR' }
  });
  return data;
};

export const fetchTemplates = async () => {
  const { data } = await api.get('/api/templates');
  return data;
};

export const fetchTemplateById = async (templateId) => {
  const { data } = await api.get(`/api/templates/${templateId}`);
  return data;
};

export const createTemplate = async (payload) => {
  const company_id = ensureCompanyId();
  const requestData = {
    company_id,
    ...payload
  };
  const { data } = await api.post('/api/templates', requestData);
  return data;
};

export const fetchDocumentTypes = async () => {
  const company_id = ensureCompanyId();
  const { data } = await api.get(`/api/documents/document-type/${company_id}`);
  return data;
};

export const sendCandidateDocumentTemplate = async (candidateId, payload) => {
  const company_id = ensureCompanyId();
  const requestData = {
    company_id,
    candidate_id: candidateId,
    source: "generated",
    ...payload
  };
  const { data } = await api.post(
    "/api/v2/documents/create",
    requestData
  );
  return data;
};

export const createCandidateDocument = async (candidateId, payload) => {
  const company_id = ensureCompanyId();
  let requestData;
  
  if (payload.source === "uploaded") {
    const formData = new FormData();
    formData.append("company_id", company_id);
    formData.append("candidate_id", candidateId);
    
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    
    const { data } = await api.post("/api/v2/documents/create", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return data;
  } else {
    requestData = {
      company_id,
      candidate_id: candidateId,
      ...payload
    };
    const { data } = await api.post("/api/v2/documents/create", requestData);
    return data;
  }
};

export const generateCandidateDocument = async (candidateId, payload) => {
  const company_id = ensureCompanyId();
  const requestData = {
    company_id,
    candidate_id: candidateId,
    source: "generated",
    ...payload
  };
  const response = await api.post(
    "/api/v2/documents/create",
    requestData,
    { responseType: 'blob' }
  );
  return response;
};
